import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { normalizeInputValue, NormalizeOptions } from 'app/shared/functions/normalizer';

/**
 * A regular expression pattern to match safe names.
 *
 * This pattern allows the use of:
 * - Unicode letters (`\p{L}`)
 * - Combining marks (`\p{M}`)
 * - Numbers (`\p{N}`)
 * - Spaces
 * - Hyphens (`-`)
 * - Single quotes (`'`)
 * - Periods (`.`)
 *
 * The `u` flag enables Unicode matching for appropriate character sets.
 */
const SAFE_NAME = /^[\p{L}\p{M}\p{N} \-'.’]+$/u;

/**
 * A regular expression pattern used to match Unicode characters that fall into
 * control characters (category Cc) or format characters (category Cf).
 *
 * The pattern utilizes Unicode property escapes (enabled by the 'u' flag) to
 * specifically identify these character categories according to the Unicode standard.
 *
 * - Control characters (Cc) are non-printable characters used for control purposes.
 * - Format characters (Cf) are characters that are invisible and used to affect
 *   the appearance or layout of text.
 */
const CTRL_OR_FORMAT = /[\p{Cc}\p{Cf}]/u;

/**
 * A regular expression to match symbols and characters that are similar to emojis.
 *
 * The `EMOJIish` regex uses Unicode property escapes:
 * - `\p{So}` matches symbols not included in letters, numbers, punctuation, or separators.
 * - `\p{Sk}` matches modifier symbols, such as combining accent marks.
 * The `u` flag is used to enable full Unicode matching.
 *
 * This regex is useful for detecting emoji-like characters or symbols in a given text string.
 */
const EMOJIish = /[\p{So}\p{Sk}]/u;

/**
 * Validates the sanity of a name by checking its length and allowed characters.
 *
 * @param {number} [maxLen=100] - The maximum allowed length of the input name.
 * @param {NormalizeOptions} [opts={ trim: true, collapseWhitespace: true, plusToSpace: false }] - Options for normalizing the input value, such as trimming, collapsing whitespace, and handling '+' characters.
 * @return {ValidatorFn} A function that applies the validation logic to an `AbstractControl` and returns validation errors if any issues are found, or `null` if the value is valid.
 */
export function nameSanityValidator(
  maxLen: number = 100,
  opts: NormalizeOptions = { trim: true, collapseWhitespace: true, plusToSpace: false }, // strict by default
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = normalizeInputValue(control.value, opts);

    if (!value) {
      return null;
    }

    if (value.length > maxLen) {
      return { tooLong: { maxLen } };
    }

    if (CTRL_OR_FORMAT.test(value)) {
      return { badControlChars: true };
    }

    if (EMOJIish.test(value)) {
      return { emojiNotAllowed: true };
    }

    if (!SAFE_NAME.test(value)) {
      return { badChars: true };
    } // covers "+" and any disallowed symbol

    return null;
  };
}
